/**
 * Obstructions Feature Constants
 *
 * Status / severity / impact color tokens use Mantine CSS variables — never
 * hardcoded hex (F-N3 BLOCKING). Type icon mapping references Tabler icon
 * component names so the renderer can `useTablerIcon` without each tile
 * importing its own set.
 */
import {
  ObstructionStatus,
  SeverityLevel,
  TrafficImpact,
  ObstructionType,
} from '@/types/obstructions';

/**
 * Status palette — uses the shared `--app-status-*` design-system tokens so the
 * obstruction lifecycle reuses the same colors as task status badges.
 *
 * Mapping rationale (see `utrudnienia_projekt.md` §9.1 + `STATUS_COLORS` for tasks):
 * - Planned/Scheduled → pending (gray-blue)
 * - Active            → progress (blue)
 * - Extended          → delayed (orange) — same semantic as a late task
 * - Completed         → completed (green)
 * - Archived          → neutral text token (no status-specific color)
 */
export const OBSTRUCTION_STATUS_COLORS: Record<ObstructionStatus, string> = {
  [ObstructionStatus.Planned]: 'var(--app-status-pending)',
  [ObstructionStatus.Scheduled]: 'var(--app-status-pending)',
  [ObstructionStatus.Active]: 'var(--app-status-progress)',
  [ObstructionStatus.Extended]: 'var(--app-status-delayed)',
  [ObstructionStatus.Completed]: 'var(--app-status-completed)',
  [ObstructionStatus.Archived]: 'var(--mantine-color-dimmed)',
} as const;

/**
 * Severity palette — escalates from neutral gray to red. Uses Mantine theme
 * tokens so dark/light themes stay consistent.
 */
export const SEVERITY_COLORS: Record<SeverityLevel, string> = {
  [SeverityLevel.Low]: 'var(--mantine-color-gray-6)',
  [SeverityLevel.Medium]: 'var(--mantine-color-yellow-6)',
  [SeverityLevel.High]: 'var(--mantine-color-orange-6)',
  [SeverityLevel.Critical]: 'var(--mantine-color-red-6)',
} as const;

/**
 * Traffic impact palette — drives the chip color shown in the list / map
 * filter UI.
 */
export const TRAFFIC_IMPACT_COLORS: Record<TrafficImpact, string> = {
  [TrafficImpact.NoRestriction]: 'var(--mantine-color-green-6)',
  [TrafficImpact.ReducedLanes]: 'var(--mantine-color-yellow-6)',
  [TrafficImpact.AlternatingTraffic]: 'var(--mantine-color-orange-6)',
  [TrafficImpact.Detour]: 'var(--mantine-color-blue-6)',
  [TrafficImpact.FullClosure]: 'var(--mantine-color-red-6)',
} as const;

/**
 * Tabler icon component names per obstruction type. The renderer resolves
 * these to icon components via the shared icon registry so tiles, badges,
 * and map markers reuse the same mapping.
 */
export const OBSTRUCTION_TYPE_ICONS: Record<ObstructionType, string> = {
  [ObstructionType.Maintenance]: 'IconTool',
  [ObstructionType.Construction]: 'IconBulldozer',
  [ObstructionType.Accident]: 'IconCarCrash',
  [ObstructionType.Closure]: 'IconBarrierBlock',
  [ObstructionType.Event]: 'IconCalendarEvent',
  [ObstructionType.Emergency]: 'IconAlertTriangle',
} as const;

/** Maximum obstruction title length (mirrors backend constant). */
export const OBSTRUCTION_TITLE_MAX_LENGTH = 150;

/** Maximum obstruction description length (mirrors backend constant). */
export const OBSTRUCTION_DESCRIPTION_MAX_LENGTH = 2000;

/** Maximum primary-location length (mirrors backend constant). */
export const OBSTRUCTION_LOCATION_MAX_LENGTH = 200;

/** Maximum affected-road location length (mirrors backend constant). */
export const OBSTRUCTION_AFFECTED_ROAD_LOCATION_MAX_LENGTH = 200;

/** Maximum affected-road description length (mirrors backend constant). */
export const OBSTRUCTION_AFFECTED_ROAD_DESCRIPTION_MAX_LENGTH = 500;

/** Maximum number of affected roads per obstruction (mirrors backend constant). */
export const OBSTRUCTION_MAX_AFFECTED_ROADS = 50;

/** Maximum internal-notes length (mirrors backend constant). */
export const OBSTRUCTION_INTERNAL_NOTES_MAX_LENGTH = 2000;

/** Maximum contractor-notes length (mirrors backend constant). */
export const OBSTRUCTION_CONTRACTOR_NOTES_MAX_LENGTH = 1000;

/** Maximum contractor email length. */
export const OBSTRUCTION_CONTRACTOR_EMAIL_MAX_LENGTH = 200;

/** Maximum contractor permit number length. */
export const OBSTRUCTION_CONTRACTOR_PERMIT_NUMBER_MAX_LENGTH = 60;

/** Maximum contractor company name length. */
export const OBSTRUCTION_CONTRACTOR_COMPANY_MAX_LENGTH = 200;

/** Maximum contractor manager name length. */
export const OBSTRUCTION_CONTRACTOR_MANAGER_MAX_LENGTH = 100;

/** Maximum contractor phone number length. */
export const OBSTRUCTION_CONTRACTOR_PHONE_MAX_LENGTH = 20;

/**
 * Phone-number input mask (`react-imask`). The `0` token accepts digits only —
 * so the unmasked value (stored via `onAccept` with `unmask`) contains nothing
 * but digits. Polish grouping (3-3-3); change this single constant to adjust
 * the format.
 */
export const PHONE_NUMBER_MASK = '000 000 000';

/**
 * Number of digits per group when formatting a phone number for display
 * (Polish 3-3-3 grouping). Single source of truth shared by the input mask and
 * the `formatPhoneNumber` display helper.
 */
export const PHONE_GROUP_SIZE = 3;

/** Default page size for the obstructions list. */
export const OBSTRUCTION_DEFAULT_PAGE_SIZE = 20;

/** Maximum page size for the obstructions list. */
export const OBSTRUCTION_MAX_PAGE_SIZE = 100;

/** Sentinel value for `all` in obstruction multi-select filters. */
export const OBSTRUCTION_FILTER_ALL = 'all';
